import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getLeagueById } from "../services/leagueService";
import { getErrorMessage } from "../services/api";
import PageHeader from "../components/PageHeader";
import SectionCard from "../components/SectionCard";
import OverviewActionCard from "../components/OverviewActionCard";
import StatPill from "../components/StatPill";
import EmptyState from "../components/EmptyState";
import AlertMessage from "../components/AlertMessage";

function LeagueOverviewPage({ userId }) {
  const { leagueId } = useParams();

  const [league, setLeague] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!userId || !leagueId) return;

    const controller = new AbortController();

    async function loadLeague() {
      setLoading(true);
      setMessage("");
      try {
        const data = await getLeagueById(Number(leagueId), { signal: controller.signal });
        setLeague(data);
      } catch (error) {
        if (error?.name === "AbortError") return;
        console.error(error);
        setMessage(getErrorMessage(error));
      } finally {
        setLoading(false);
      }
    }

    loadLeague();

    return () => controller.abort();
  }, [userId, leagueId]);

  const getTournamentInfo = (type) => {
    if (type === "WorldCup") {
      return {
        label: "🏆 World Cup",
        description: "12 rounds of World Cup predictions",
        bgColor: "bg-amber-50",
        textColor: "text-amber-700",
        borderColor: "border-amber-200",
      };
    }
    return {
      label: "⚽ Premier League",
      description: "38 gameweeks of Premier League predictions",
      bgColor: "bg-emerald-50",
      textColor: "text-emerald-700",
      borderColor: "border-emerald-200",
    };
  };

  if (!userId) {
    return (
      <SectionCard>
        <p className="text-slate-600">Loading user...</p>
      </SectionCard>
    );
  }

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="h-9 w-40 animate-pulse rounded-full bg-slate-200" />
        <div className="rounded-3xl bg-gradient-to-r from-slate-900 to-slate-800 p-6 sm:p-8">
          <div className="h-3 w-16 animate-pulse rounded bg-slate-700" />
          <div className="mt-3 h-8 w-48 animate-pulse rounded bg-slate-700" />
          <div className="mt-2 h-4 w-64 animate-pulse rounded bg-slate-700" />
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-2xl bg-slate-50 px-4 py-4 ring-1 ring-slate-200">
              <div className="h-3 w-16 animate-pulse rounded bg-slate-200" />
              <div className="mt-2 h-6 w-24 animate-pulse rounded bg-slate-200" />
            </div>
          ))}
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
              <div className="h-5 w-28 animate-pulse rounded bg-slate-200" />
              <div className="mt-2 h-4 w-full animate-pulse rounded bg-slate-100" />
              <div className="mt-1 h-4 w-3/4 animate-pulse rounded bg-slate-100" />
              <div className="mt-5 h-11 animate-pulse rounded-2xl bg-slate-100" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!league) {
    return (
      <div className="space-y-6">
        <div>
          <Link
            to="/leagues"
            className="inline-flex items-center rounded-full border border-slate-300 bg-white px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-slate-700 transition hover:bg-slate-50"
          >
            ← Back to My Leagues
          </Link>
        </div>

        <AlertMessage message={message} type="error" />

        <EmptyState
          title="League not found"
          description="We couldn't load this league right now."
        />
      </div>
    );
  }

  const tournamentInfo = getTournamentInfo(league.tournamentType);

  return (
    <div className="space-y-8">
      <div>
        <Link
          to="/leagues"
          className="inline-flex items-center rounded-full border border-slate-300 bg-white px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-slate-700 transition hover:bg-slate-50"
        >
          ← Back to My Leagues
        </Link>
      </div>

      <PageHeader
        eyebrow="League"
        title={league.name}
        subtitle={tournamentInfo.description}
        dark
        rightContent={
          <div className="flex flex-col items-end gap-2">
            <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${tournamentInfo.bgColor} ${tournamentInfo.textColor} border ${tournamentInfo.borderColor}`}>
              {tournamentInfo.label}
            </span>
            <div className="rounded-2xl bg-white/10 px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-white">
              Invite code: <span className="font-semibold break-all">{league.inviteCode}</span>
            </div>
          </div>
        }
      />

      <AlertMessage message={message} type="error" />

      <section className="grid gap-4 sm:grid-cols-3">
        <StatPill label="Invite Code" value={league.inviteCode} />
        <StatPill label="Members" value={league.memberCount} />
        <StatPill label="Status" value={league.isActive ? "Active" : "Inactive"} />
      </section>

      <section>
        <div className="mb-4">
          <h3 className="text-lg sm:text-xl font-semibold tracking-tight text-slate-900">Quick Actions</h3>
          <p className="mt-1 text-xs sm:text-sm text-slate-600">
            Jump into the most important areas of this league.
          </p>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <OverviewActionCard
            title="Make Picks"
            description="Open the current gameweek and submit your score predictions before kickoff."
            to={`/leagues/${leagueId}/picks`}
            state={{ league }}
            cta="Open Picks"
          />
          <OverviewActionCard
            title="League Table"
            description="View rankings, total points, and how everyone in the league is performing."
            to={`/leagues/${leagueId}/table`}
            state={{ league }}
            cta="Open Table"
          />
          <OverviewActionCard
            title="History"
            description="Review earlier gameweeks, compare past predictions, and revisit finished fixtures."
            to={`/leagues/${leagueId}/history`}
            state={{ league }}
            cta="Open History"
          />
        </div>
      </section>

      <SectionCard title="League Details" subtitle="A lightweight summary of the current league.">
        <dl className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl bg-slate-50 p-3 sm:p-4">
            <dt className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              League Name
            </dt>
            <dd className="mt-2 text-sm sm:text-base font-semibold text-slate-900 break-words">{league.name}</dd>
          </div>
          <div className="rounded-2xl bg-slate-50 p-3 sm:p-4">
            <dt className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Tournament Type
            </dt>
            <dd className="mt-2 text-sm sm:text-base font-semibold text-slate-900">{tournamentInfo.label}</dd>
          </div>
          <div className="rounded-2xl bg-slate-50 p-3 sm:p-4">
            <dt className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Invite Code
            </dt>
            <dd className="mt-2 text-sm sm:text-base font-semibold text-slate-900 break-all">{league.inviteCode}</dd>
          </div>
          <div className="rounded-2xl bg-slate-50 p-3 sm:p-4">
            <dt className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Members
            </dt>
            <dd className="mt-2 text-sm sm:text-base font-semibold text-slate-900">{league.memberCount}</dd>
          </div>
        </dl>
      </SectionCard>
    </div>
  );
}

export default LeagueOverviewPage;
